import type { Event, Breadcrumb, ReplayFrameEvent } from '@sentry/react';

/**
 * PHI / credential scrubbing for everything that leaves the app through Sentry.
 *
 * URLs in this app carry patient ids (`/dashboard/details/:patientId`, API
 * paths) and password-reset credentials (`/password/reset/confirm/:uid/:token`).
 * These helpers strip query strings and fragments and replace id-like path
 * segments so they never reach Sentry in request URLs, transaction names,
 * breadcrumbs, spans, exception messages or session-replay frames.
 *
 * Sentry's hooks are allowed to mutate the objects they receive, so these
 * functions mutate in place and return the same object (or null to drop it).
 */

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NUMERIC_RE = /^\d+$/;
// Long opaque tokens (base64url, hex, ...). Every static route word in this app is shorter.
const TOKEN_RE = /^[A-Za-z0-9_-]{16,}$/;
// Static route segments that would otherwise look like ids.
const STATIC_SEGMENTS = new Set(['404']);
// Under `/password/...` everything except these words is a reset uid or token.
const PASSWORD_ROUTE_WORDS = new Set(['reset', 'confirm']);
// Absolute URLs or absolute paths embedded in free text.
const URL_IN_TEXT_RE = /(?:[a-z][a-z0-9+.-]*:\/\/|\/)[^\s"'<>]+/gi;

export const ID_PLACEHOLDER = '[id]';
export const REDACTED = '[redacted]';

// Keys whose string value is a URL and must be scrubbed.
const URL_KEYS = [
  'url',
  'http.url',
  'url.full',
  'url.path',
  'http.target',
  'from',
  'to',
  'previous',
  'current',
  'previousRoute',
  'route',
];
// Keys that must never be sent at all (queries, fragments, bodies).
const DROPPED_KEYS = [
  'http.query',
  'url.query',
  'http.fragment',
  'url.fragment',
  'query_string',
  'http.request.body',
  'http.response.body',
  'request_body',
  'response_body',
  'body',
];

/** Replace id-like segments of a URL path. Route patterns (`:patientId`) are left alone. */
export function scrubPath(path: string): string {
  let inPasswordRoute = false;

  return path
    .split('/')
    .map((segment) => {
      if (!segment) return segment;
      if (inPasswordRoute) return PASSWORD_ROUTE_WORDS.has(segment) ? segment : REDACTED;
      if (segment === 'password') {
        inPasswordRoute = true;
        return segment;
      }
      if (STATIC_SEGMENTS.has(segment)) return segment;
      if (UUID_RE.test(segment) || NUMERIC_RE.test(segment) || TOKEN_RE.test(segment)) {
        return ID_PLACEHOLDER;
      }
      return segment;
    })
    .join('/');
}

/** Drop query string and fragment, scrub the path. Works for absolute URLs and bare paths. */
export function scrubUrl(url: string): string {
  const [withoutQueryOrFragment] = url.split(/[?#]/);
  const absolute = withoutQueryOrFragment.match(/^([a-z][a-z0-9+.-]*:\/\/[^/]*)(\/.*)?$/i);

  if (absolute) return absolute[1] + scrubPath(absolute[2] ?? '');
  return scrubPath(withoutQueryOrFragment);
}

/** Scrub every URL or absolute path that appears inside free text (span descriptions, error messages). */
export function scrubText(text: string): string {
  return text.replace(URL_IN_TEXT_RE, (url) => scrubUrl(url));
}

/** Scrub URL-bearing keys and delete query/body keys on an arbitrary data bag. */
export function scrubData<T extends object | undefined>(data: T): T {
  if (!data) return data;
  const record = data as Record<string, unknown>;

  DROPPED_KEYS.forEach((key) => {
    delete record[key];
  });
  URL_KEYS.forEach((key) => {
    const value = record[key];
    if (typeof value === 'string') record[key] = scrubUrl(value);
  });

  return data;
}

/** `beforeBreadcrumb`: drop console output entirely, scrub URLs in fetch/xhr/navigation crumbs. */
export function scrubBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb | null {
  if (breadcrumb.category === 'console') return null;
  if (typeof breadcrumb.message === 'string') breadcrumb.message = scrubText(breadcrumb.message);
  scrubData(breadcrumb.data);
  return breadcrumb;
}

/** `beforeSend` / `beforeSendTransaction`: scrub an error or transaction event in place. */
export function scrubEvent<E extends Event>(event: E): E {
  if (event.request) {
    if (event.request.url) event.request.url = scrubUrl(event.request.url);
    delete event.request.query_string;
    delete event.request.data;
    delete event.request.cookies;
    const { headers } = event.request;
    if (headers?.Referer) headers.Referer = scrubUrl(headers.Referer);
  }

  if (event.transaction) event.transaction = scrubText(event.transaction);
  if (typeof event.message === 'string') event.message = scrubText(event.message);

  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map(scrubBreadcrumb).filter((crumb): crumb is Breadcrumb => crumb !== null);
  }

  scrubData(event.contexts?.trace?.data);

  event.spans?.forEach((span) => {
    if (span.description) span.description = scrubText(span.description);
    scrubData(span.data);
  });

  event.exception?.values?.forEach((exception) => {
    if (exception.value) exception.value = scrubText(exception.value);
  });

  return event;
}

/** Replay `beforeAddRecordingEvent`: same treatment for the breadcrumbs and spans recorded into a replay. */
export function scrubReplayFrame(frame: ReplayFrameEvent): ReplayFrameEvent | null {
  if (frame.data.tag === 'breadcrumb') {
    const { payload } = frame.data;
    if (payload.category === 'console') return null;
    if (typeof payload.message === 'string') payload.message = scrubText(payload.message);
    scrubData(payload.data);
  } else if (frame.data.tag === 'performanceSpan') {
    const { payload } = frame.data;
    if (payload.description) payload.description = scrubText(payload.description);
    scrubData(payload.data);
  }

  return frame;
}
